// Centralized API endpoint paths for the entire project
export const API_ENDPOINTS = {
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    user: '/auth/user',
    pwdUpdate: '/my-account/password',
    pwdReset: '/auth/reset-password',
    pwdForgot: '/auth/forgot-password',
  },
  register: {
    validate: '/register/validate',
    branches: '/register/branches',
    plans: '/register/plans',
    checkout: (queryString: string) => `/register/checkout?${queryString}`,
    checkStatus: `/payments/status/check`,
    verifyEmail: (queryString: string) => `/register/verify-email${queryString}`,
    resendEmailVerification: `/register/resend-verification`,
  },
  gymOperations: {
    base: '/gym-operations',
    closeGym: '/gym-operations/close',
    openGym: '/gym-operations/reopen',
    gymStatus: '/gym-operations/status',
  },
  assistants: {
    base: '/assistants',
    export: '/assistants',
    byId: (id: string | number) => `/assistants/${id}`,
    sendResetLink: (id: string | number) => `/assistants/${id}/send-reset-link`,
  },
  gymManagers: {
    base: '/gym-managers',
    export: '/gym-managers',
    byId: (id: string | number) => `/gym-managers/${id}`,
    sendResetLink: (id: string | number) => `/gym-managers/${id}/send-reset-link`,
  },
  coaches: {
    base: '/coaches',
    export: '/coaches',
    byId: (id: string | number) => `/coaches/${id}`,
    sendResetLink: (id: string | number) => `/coaches/${id}/send-reset-link`,
  },
  members: {
    base: '/members',
    export: '/members/export',
    import: '/members/import',
    byId: (id: string | number) => `/members/${id}`,
    ban: (id: string | number) => `/members/${id}/ban`,
    unban: (id: string | number) => `/members/${id}/unban`,
    softDelete: (id: string | number) => `/members/${id}/soft-delete`,
    profile: (id: string | number) => `/members/${id}/profile`,
    account: (id: string | number) => `/members/${id}/account`,
    membership: (id: string | number) => `/members/${id}/membership`,
    preferences: (id: string | number) => `/members/${id}/preferences`,
    bookings: (id: string | number) => `/members/${id}/bookings`,
    payments: (id: string | number) => `/members/${id}/payments`,
    checkins: (id: string | number) => `/members/${id}/checkins`,
    eligibleCheckins: (id: string | number) => `/members/${id}/checkins/eligible`,
    forcePasswordReset: (id: string) => `/members/${id}/force-password-reset`,
    forceLogout: (id: string) => `/members/${id}/force-logout`,
    sendResetLink: (id: string | number) => `/members/${id}/send-reset-link`,
    sendVerificationEmail: (id: string) => `/members/${id}/send-verification-email`,
    civilIdPhotos: (id: string | number) => `/members/${id}/civil-id-photos`,
    uploadCivilIdPhoto: (id: string | number) => `/members/${id}/civil-id-photo`,
    deleteCivilIdPhoto: (id: string | number) => `/members/${id}/civil-id-photo`,
    attendanceSummary: (id: string | number) => `/members/${id}/attendance/summary`,
    attendanceTrends: (id: string | number) => `/members/${id}/attendance/trends`,
    attendanceByClassType: (id: string | number) => `/members/${id}/attendance/by-class-type`,
    score: (id: string | number) => `/members/${id}/score`,
    recalculateScore: (id: string | number) => `/members/${id}/score/recalculate`,
  },
  classes: {
    base: '/classes',
    byId: (id: string | number) => `/classes/${id}`,
    classesBookings: (id: string | number) => `/classes/${id}/bookings`,
    query: (queryString: string) => `/classes?${queryString}`,
  },
  classTypes: {
    base: '/class-types',
    browse: '/class-types/browse',
    byId: (id: string | number) => `/class-types/${id}`,
    query: (queryString: string) => `/class-types?${queryString}`,
    logo: (id: string | number) => `/class-types/${id}/logo`,
  },
  classTemplates: {
    base: '/class-templates',
    byId: (id: string | number) => `/class-templates/${id}`,
    generate: (id: string | number) => `/class-templates/${id}/generate`,
    query: (queryString: string) => `/class-templates?${queryString}`,
  },
  bookings: {
    base: '/bookings',
    today: '/bookings/today',
    myCancel: (id: string | number) => `/my-bookings/${id}/cancel`,
    byId: (id: string | number) => `/bookings/${id}`,
    cancel: (id: string | number) => `/bookings/${id}/cancel`,
    query: (queryString: string) => `/bookings?${queryString}`,
    export: '/bookings/export',
  },
  myBookings: {
    base: '/my-bookings',
    byId: (id: string | number) => `/my-bookings/${id}`,
    past: `/my-bookings/past`,
    upcoming: `/my-bookings/upcoming`,
    stats: `/my-bookings/stats`,
    cancel: (id: string | number) => `/my-bookings/${id}/cancel`,
    query: (queryString: string) => `/my-bookings?${queryString}`,
  },
  plans: {
    base: '/plans',
    active: '/plans/active',
    byId: (id: string | number) => `/plans/${id}`,
    query: (queryString: string) => `/plans?${queryString}`,
  },
  branches: {
    base: '/branches',
    export: '/branches/export',
    switcher: '/branches/switcher',
    byId: (id: string | number) => `/branches/${id}`,
    branchPlans: (id: string | number) => `/branches/${id}/plans`,
    query: (queryString: string) => `/branches?${queryString}`,
    logo: (id: string | number) => `/branches/${id}/logo`,
    banner: (id: string | number) => `/branches/${id}/banner`,
  },
  payments: {
    base: '/payments',
    export: '/payments/export',
    initiate: '/payments/initiate',
    byId: (id: string | number) => `/payments/${id}`,
    query: (queryString: string) => `/payments?${queryString}`,
  },
  memberPayments: {
    initiate: '/member-payments/initiate',
    checkStatus: '/member-payments/status/check',
  },
  myPayments: {
    base: '/my-payments',
    latest: '/my-payments/latest',
    export: '/my-payments/export',
    query: (queryString: string) => `/my-payments?${queryString}`,
  },
  myNotes: {
    base: '/my-notes',
    latest: '/my-notes/latest',
    byId: (id: string | number) => `/my-notes/${id}`,
    query: (queryString: string) => `/my-notes?${queryString}`,
  },
  myClasses: {
    base: '/my-classes',
    available: (queryString: string) => `/my-classes/available?${queryString}`,
    byId: (id: string | number) => `/my-classes/${id}`,
    query: (queryString: string) => `/my-classes?${queryString}`,
  },
  myAccount: {
    base: '/my-account',
    deleteRequest: '/my-account/delete-request',
    deletionStatus: '/my-account/deletion-status',
    cancelDeletion: '/my-account/cancel-deletion',
    updatePassword: '/my-account/password',
    requestEmailChange: '/my-account/email/request-change',
    pendingEmail: '/my-account/email/pending',
    cancelEmailChange: '/my-account/email/cancel',
    query: (queryString: string) => `/my-account?${queryString}`,
  },
  roles: {
    permissions: '/roles/permissions',
  },
  user: {
    requestDelete: '/user/request-delete',
    softDelete: '/user/soft-delete',
    profile: '/user/profile',
    settings: '/user/settings',
    avatar: {
      upload: '/user/avatar',
      delete: '/user/avatar',
    },
  },
  userDeletion: {
    base: '/user-deletion',
    stats: '/user-deletion/stats',
    process: (id: string | number) => `/user-deletion/${id}/process`,
    cancel: (id: string | number) => `/user-deletion/${id}/cancel`,
    byId: (id: string | number) => `/user-deletion/${id}`,
    query: (queryString: string) => `/user-deletion?${queryString}`,
  },
  checkin: {
    search: '/checkin/search',
  },
  checkins: {
    base: '/checkins',
    today: '/checkins/today',
    byId: (id: string | number) => `/checkins/${id}`,
    query: (queryString: string) => `/checkins?${queryString}`,
    export: '/checkins/export',
    actions: {
      bookingQr: '/checkins/booking_qr',
      pin: '/checkins/pin',
      memberQr: '/checkins/qr',
      manual: '/checkins/manual',
      manualForClass: (classId: string | number) => `/classes/${classId}/checkins/manual`,
      memberQrEligible: `/checkins/qr/eligible`,
    },
  },
  appSettings: {
    base: '/app-settings',
    public: '/app-settings/public',
    reset: `/app-settings/reset`,
    query: (queryString: string) => `/app-settings?${queryString}`,
    branding: {
      appLogo: '/app-settings/branding/app-logo',
      mobileLogo: '/app-settings/branding/mobile-logo',
      favicon: '/app-settings/branding/favicon',
      mailLogo: '/app-settings/branding/mail-logo',
    },
  },
  bannedIdentities: {
    base: '/banned-identities',
    unban: (id: string | number) => `/banned-identities/${id}/unban`,
    byId: (id: string | number) => `/banned-identities/${id}`,
    query: (queryString: string) => `/banned-identities?${queryString}`,
  },
  activityLogs: {
    base: '/activity-logs',
  },
  health: {
    base: (memberId: string | number) => `/members/${memberId}/health`,
    vitals: {
      base: (memberId: string | number) => `/members/${memberId}/health/vitals`,
      byId: (memberId: string | number, id: string | number) => `/members/${memberId}/health/vitals/${id}`,
      query: (memberId: string | number, queryString: string) => `/members/${memberId}/health/vitals?${queryString}`,
    },
    measurements: {
      base: (memberId: string | number) => `/members/${memberId}/health/measurements`,
      byId: (memberId: string | number, id: string | number) => `/members/${memberId}/health/measurements/${id}`,
      query: (memberId: string | number, queryString: string) =>
        `/members/${memberId}/health/measurements?${queryString}`,
    },
    goals: {
      base: (memberId: string | number) => `/members/${memberId}/health/goals`,
      byId: (memberId: string | number, id: string | number) => `/members/${memberId}/health/goals/${id}`,
      query: (memberId: string | number, queryString: string) => `/members/${memberId}/health/goals?${queryString}`,
    },
    notes: {
      base: (memberId: string | number) => `/members${memberId}/health/notes`,
      byId: (memberId: string | number, id: string | number) => `/members/${memberId}/health/notes/${id}`,
      query: (memberId: string | number, queryString: string) => `/members/${memberId}/health/notes?${queryString}`,
    },
    progressPhotos: {
      base: (memberId: string | number) => `/members/${memberId}/health/progress/photos`,
      byId: (memberId: string | number, id: string | number) => `/members/${memberId}/health/progress/photos/${id}`,
      query: (memberId: string | number, queryString: string) =>
        `/members/${memberId}/health/progress/photos?${queryString}`,
      share: (memberId: string | number, id: string | number) =>
        `/members/${memberId}/health/progress/photos/${id}/share`,
      unshare: (memberId: string | number, id: string | number) =>
        `/members/${memberId}/health/progress/photos/${id}/share`,
    },
  },
  systemHealth: {
    base: '/system-health',
  },
  myHealth: {
    base: '/my-health',
    vitals: {
      base: '/my-health/vitals',
      byId: (id: string | number) => `/my-health/vitals/${id}`,
      query: (queryString: string) => `/my-health/vitals?${queryString}`,
    },
    measurements: {
      base: '/my-health/measurements',
      byId: (id: string | number) => `/my-health/measurements/${id}`,
      query: (queryString: string) => `/my-health/measurements?${queryString}`,
    },
    goals: {
      base: '/my-health/goals',
      byId: (id: string | number) => `/my-health/goals/${id}`,
      query: (queryString: string) => `/my-health/goals?${queryString}`,
    },
    notes: {
      base: '/my-health/notes',
      byId: (id: string | number) => `/my-health/notes/${id}`,
      query: (queryString: string) => `/my-health/notes?${queryString}`,
    },
    progressPhotos: {
      base: '/my-health/progress-photos',
      byId: (id: string | number) => `/my-health/progress-photos/${id}`,
      query: (queryString: string) => `/my-health/progress-photos?${queryString}`,
    },
  },
  reports: {
    membership: {
      summary: () => `/reports/memberships/summary`,
      trends: () => `/reports/memberships/trends`,
      topPlans: () => `/reports/memberships/top-plans`,
      distribution: () => `/reports/memberships/distribution`,
      members: () => `/reports/memberships/members`,
      byBranch: () => `/reports/memberships/by-branch`,
      exportCsv: () => `/reports/memberships/export`,
    },
    bookings: {
      summary: () => `/reports/bookings/summary`,
      chart: () => `/reports/bookings/chart`,
      trends: () => `/reports/bookings/trends`,
      byClassType: () => `/reports/bookings/by-class-type`,
      byBranch: () => `/reports/bookings/by-branch`,
      distribution: () => `/reports/bookings/distribution`,
      recent: () => `/reports/bookings/recent`,
      exportCsv: () => `/reports/bookings/export`,
    },
    financial: {
      summary: () => `/reports/financial/summary`,
      revenueByPlan: () => `/reports/financial/revenue-by-plan`,
      byBranch: () => `/reports/financial/by-branch`,
      paymentMethods: () => `/reports/financial/payment-methods`,
      recentPayments: () => `/reports/financial/recent-payments`,
      exportCsv: () => `/reports/financial/export`,
    },
    attendance: {
      summary: () => `/reports/attendance/summary`,
      trends: () => `/reports/attendance/trends`,
      byClassType: () => `/reports/attendance/by-class-type`,
      byBranch: () => `/reports/attendance/by-branch`,
      recent: () => `/reports/attendance/recent`,
      exportCsv: () => `/reports/attendance/export`,
    },
    members: {
      engagement: () => `/reports/members/engagement`,
      notBooking: () => `/reports/members/not-booking`,
    },
    gymManager: {
      summary: '/reports/gym-manager/summary',
    },
  },
  stats: {
    adminSummary: () => `admin/stats/summary`,
    managerSummary: () => `gym-manager/stats/summary`,
    assistantSummary: () => `assistant/stats/summary`,
    memberSummary: () => `member/stats/summary`,
  },
  adminUsers: {
    suspend: (id: string | number) => `/admin/users/${id}/suspend`,
    unsuspend: (id: string | number) => `/admin/users/${id}/unsuspend`,
    ban: (id: string | number) => `/admin/users/${id}/ban`,
    unban: (id: string | number) => `/admin/users/${id}/unban`,
    deactivate: (id: string | number) => `/admin/users/${id}/deactivate`,
    restore: (id: string | number) => `/admin/users/${id}/restore`,
    reactivate: (id: string | number) => `/admin/users/${id}/reactivate`,
    actionBlockers: (id: string | number) => `/admin/users/${id}/action-blockers`,
  },
  support: {
    base: '/support-tickets',
  },
  memberships: {
    stats: '/memberships/stats',
    bulkPause: '/memberships/bulk-pause',
    bulkResume: '/memberships/bulk-resume',
  },
} as const
